import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Split button — primary action on the left, dropdown caret on the right.
 *
 * Pure presentational; both halves are independently styled via Tailwind
 * classes passed by the caller so the component can adopt any module's
 * accent (lime, sky, …) without baking colour decisions in here.
 *
 * Items in the menu close the dropdown automatically after `onSelect`.
 */
export interface SplitButtonItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  onSelect: () => void;
}

export function SplitButton({
  primaryLabel,
  primaryIcon,
  primaryAriaLabel,
  onPrimary,
  items,
  disabled,
  /** Tailwind classes for both the primary part and the caret part. */
  primaryClassName,
  caretClassName,
  menuAlign = "right",
}: {
  primaryLabel: string;
  primaryIcon?: React.ReactNode;
  primaryAriaLabel?: string;
  onPrimary: () => void;
  items: SplitButtonItem[];
  disabled?: boolean;
  primaryClassName: string;
  caretClassName: string;
  menuAlign?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<HTMLButtonElement>(null);
  /** Anchor rect captured when the menu opens so the portal can position. */
  const [anchor, setAnchor] = useState<{
    top: number;
    left: number;
    right: number;
  } | null>(null);

  // Dismiss on outside click + Esc — keeps the dropdown well-behaved without
  // a portal layer.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Recompute anchor rect when the menu opens or the viewport changes —
  // ensures the portal-rendered menu hugs the caret on resize/scroll.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = caretRef.current?.getBoundingClientRect();
      if (r) setAnchor({ top: r.bottom + 8, left: r.left, right: r.right });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={onPrimary}
        disabled={disabled}
        aria-label={primaryAriaLabel ?? primaryLabel}
        className={primaryClassName}
      >
        {primaryIcon}
        {primaryLabel}
      </button>
      <button
        ref={caretRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Weitere Optionen"
        className={caretClassName}
      >
        <ChevronDown className={`size-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open &&
        anchor &&
        createPortal(
          <div
            role="menu"
            style={{
              position: "fixed",
              top: anchor.top,
              ...(menuAlign === "right"
                ? { right: window.innerWidth - anchor.right }
                : { left: anchor.left }),
            }}
            className="z-200 min-w-55 overflow-hidden rounded-2xl border border-white/10 bg-[#1a1f24]/95 shadow-2xl backdrop-blur"
          >
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  item.onSelect();
                  setOpen(false);
                }}
                className="flex w-full items-start gap-3 px-3 py-2.5 text-left text-body-sm transition-colors hover:bg-white/10"
              >
                {item.icon ? (
                  <span className="mt-0.5 shrink-0 text-white/80">{item.icon}</span>
                ) : null}
                <span className="flex flex-col">
                  <span className="font-medium text-white">{item.label}</span>
                  {item.description ? (
                    <span className="text-[11px] text-muted-foreground">{item.description}</span>
                  ) : null}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
